import { useEffect, type CSSProperties } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import type { OrganizationSummary } from '../lib/types';
import { useAuth } from '../store/auth';
import { useWorkspace } from '../store/workspace';
import { navFor } from './nav';
import { CandidateFrame } from './CandidateFrame';
import { initials } from '../lib/when';
import { CentreSwitcher } from './CentreSwitcher';
import { Icon } from './Icon';
import { TargetMark } from './TargetMark';
import { roleName } from '../lib/activity';
import { hueFor } from './Avatar';

/** Staff only — a candidate does not get a sidebar. See CandidateFrame. */
export function Shell() {
  const { user, organization, signOut } = useAuth();
  const { organizationId, organizationName, leftDeliberately, choose } = useWorkspace();
  const navigate = useNavigate();

  /**
   * The administrator opens their own library without being asked to.
   *
   * They belong to no centre, so every teaching screen was locked until they
   * went to Organisations and opened somebody else's — which read as the
   * platform being broken for the person who owns it, and meant their own
   * papers were imported into a customer's library.
   *
   * Not when they have deliberately closed a centre: that is a choice, and
   * reopening one under them would undo it.
   */
  useEffect(() => {
    if (user?.role !== 'SUPER_ADMIN' || organizationId || leftDeliberately) return;
    api
      .get<{ organizations: OrganizationSummary[] }>('/organizations')
      .then(({ data }) => {
        const home = data.organizations.find((org) => org.isPlatform);
        if (home) choose({ id: home.id, name: home.name });
      })
      .catch(() => undefined);
  }, [user?.role, organizationId, leftDeliberately, choose]);

  if (!user) return null;

  /**
   * Closing the centre, and going somewhere that still works.
   *
   * Half the teaching screens are scoped to a centre, so leaving one while
   * standing on the library left the administrator looking at an error about
   * an organization they had just closed. The list of centres is where they
   * were going anyway.
   */
  function leaveCentre() {
    choose(null);
    navigate('/admin/organizations');
  }

  // A candidate sits in their own window, not the staff console. Branching
  // here rather than in the routes keeps every screen a student can reach —
  // their tests, their results, their account — inside the same frame.
  if (user.role === 'STUDENT') return <CandidateFrame />;

  const isAdmin = user.role === 'SUPER_ADMIN';

  /**
   * An administrator working inside a centre gets that centre's screens — the
   * same components a teacher uses, scoped by the chosen organisation, so
   * there is no second set of admin pages to keep in step with the first.
   * Before one is open the screens are listed but locked, never hidden.
   */
  const groups = navFor(user.role, Boolean(organizationId));

  // Falls back to the platform's own name only for the super-admin, who
  // belongs to no organisation. A student always sees their centre's.
  const name = organization?.name ?? 'Target';

  return (
    <div className="shell">
      <aside className="rail">
        <div className="rail__brand">
          {organization?.logoUrl ? (
            <img className="rail__logo" src={organization.logoUrl} alt={name} />
          ) : (
            <>
              {organization ? (
                <span className="rail__mark" aria-hidden="true">
                  <Icon name="cap" size={15} />
                </span>
              ) : (
                <TargetMark size={22} className="rail__target" />
              )}
              <span className="rail__wordmark">{name}</span>
            </>
          )}
        </div>

        {/* The day, as a desk diary would open on it. */}
        <p className="rail__today">
          <b>{new Date().toLocaleDateString('en-GB', { weekday: 'long' })}</b>
          {' · '}
          {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}
        </p>

        {/* Which centre these screens belong to, directly under the platform's
            own mark and above the screens it scopes — where the answer to
            "whose library am I looking at?" is looked for. It used to sit at
            the bottom of the rail, under the nav it was the context for. */}
        {isAdmin && organizationId && (
          <div className="scope">
            <CentreSwitcher onLeave={leaveCentre} name={organizationName ?? ''} />
          </div>
        )}

        <nav className="rail__nav" aria-label="Main">
          {groups.map((group) => (
            <div key={group.section}>
              <p className="eyebrow rail__section">{group.section}</p>
              {group.items.map((item) =>
                group.locked ? (
                  <span key={item.to} className="navlink navlink--locked" aria-disabled="true">
                    <Icon name={item.icon} className="navlink__icon" />
                    <span className="navlink__label">{item.label}</span>
                    <Icon name="lock" size={14} className="navlink__lock" />
                  </span>
                ) : (
                  <NavLink key={item.to} to={item.to} className="navlink">
                    <Icon name={item.icon} className="navlink__icon" />
                    <span className="navlink__label">{item.label}</span>
                  </NavLink>
                ),
              )}
              {group.locked && group.hint && <p className="rail__hint">{group.hint}</p>}
            </div>
          ))}
        </nav>

        <div className="rail__foot">
          {/* A password chosen by someone else is worth interrupting for: anyone
              who heard it can sign in. The prompt persists until it is replaced. */}
          {user.mustChangePassword && (
            <NavLink to="/account" className="rail__prompt">
              Choose your own password
            </NavLink>
          )}

          <div className="me">
            {/* The person is the way to their account: one thing to point at,
                rather than a name above two small buttons. */}
            <NavLink to="/account" className="me__who" title="Your account">
              <span
                className="me__avatar"
                aria-hidden="true"
                style={{ '--hue': hueFor(user.fullName) } as CSSProperties}
              >
                {initials(user.fullName)}
              </span>
              <span className="me__text">
                <span className="me__name">{user.fullName}</span>
                <span className="me__role">{roleName(user.role)} · Account</span>
              </span>
            </NavLink>
            <button type="button" className="me__out" onClick={() => void signOut()} title="Sign out">
              <Icon name="signOut" size={17} label="Sign out" />
            </button>
          </div>
        </div>
      </aside>

      {/*
        Keyed by the centre, so switching one for another rebuilds the screen
        underneath rather than leaving the last centre's students on it. The
        alternative is every scoped page remembering to watch the workspace,
        and the one that forgets shows another centre's data — which is worse
        than a wasted render. An instructor has one centre for the life of the
        session, so for them this key never changes.
      */}
      <main className="main" key={organizationId ?? 'platform'}>
        <Outlet />
      </main>
    </div>
  );
}
